import React from "react";
import type { HeatmapData} from "../utils/chartData";
import type { DrillClick } from "../utils/drilldown";
import { heatmapCellKey } from "../utils/chartData";
import {
  scoreColorScale,
  readableTextColor,
  SCORE_GRADIENT_CSS,
} from "../utils/colorScale";
import { ChartSkeleton } from "../../../ui/ChartSkeleton";
import { EmptyState } from "../../../ui/EmptyState";
import { useChartTheme } from "../../theme";
// Direct imports (not the feature barrel) to match the other chart components,
// which avoid barrels so jest doesn't transitively pull in import.meta modules.
import { TagValue } from "../../tag-aliases/TagValue";
import { formatTagValue } from "../../tag-aliases/formatTagValue";
import { useTagAliases } from "../../tag-aliases/context";
import { ATTENTION_SCORE_THRESHOLD } from "../constants";

interface HeatmapChartProps {
  data: HeatmapData;
  groupByKey: string;
  /** criteria → display label (evaluator name) for row labels. */
  labels?: Record<string, string>;
  loading?: boolean;
  /** When provided, clicking a populated cell drills into the filtered Traces view. */
  onDrill?: (click: DrillClick) => void;
}

const truncate = (s: string, n: number) =>
  s.length > n ? `${s.slice(0, n - 1)}…` : s;

const ROW_LABEL_W = 150;
const COL_HEADER_H = 28;
const CELL_W = 76;
const CELL_H = 40;

/**
 * Heatmap of average score across criteria (rows) × tag values (columns). Cells
 * are coloured on a continuous red→amber→green scale and print their score;
 * pairs with no data are left blank (never coloured as score 0). A plain SVG grid
 * — Recharts has no heatmap primitive — inside a horizontally-scrollable
 * container. Collapses the time axis via bucketRowsToHeatmapCells, so it shares
 * the grouped fetch with the time-series and issues no request of its own.
 */
export function HeatmapChart({
  data,
  groupByKey,
  labels = {},
  loading = false,
  onDrill,
}: HeatmapChartProps) {
  const chart = useChartTheme();
  // Display only: `group` remains the cell key, the drill-down value and the
  // `data-group` identity throughout.
  const { aliases } = useTagAliases();
  const groupLabel = (group: string) => formatTagValue(group, aliases);

  if (loading) {
    return <ChartSkeleton title="Average score by group" variant="bar" />;
  }

  if (data.criteria.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <EmptyState
          message="No data for this grouping"
          description={`No evaluations are tagged with “${groupByKey}” for the current filters.`}
          variant="info"
        />
      </div>
    );
  }

  const { criteria, groups, cells } = data;

  // A single-column heatmap is pointless — with < 2 groups fall back to a
  // per-criterion scorecard (horizontal bars + the attention-threshold marker).
  // Clicking a bar drills into that criterion's traces. Covers a grouping that
  // resolves to one value (e.g. filtered to a single agent) and, once M5 lands,
  // the ungrouped default.
  if (groups.length < 2) {
    const group = groups[0] ?? "";
    return (
      <div
        className="bg-card rounded-lg shadow-sm border border-border p-4"
        data-testid="heatmap-scorecard"
      >
        <h3 className="text-sm font-semibold">Average score by criteria</h3>
        <p className="text-xs text-muted-foreground mt-1 mb-3">
          {groups.length === 1
            ? `Only one “${groupByKey}” value (${groupLabel(group)}) — showing scores by criterion.`
            : "Scores by criterion."}{" "}
          Marker = {ATTENTION_SCORE_THRESHOLD.toFixed(2)} threshold.
        </p>

        <div className="flex flex-col gap-2">
          {criteria.map((crit) => {
            const cell = cells.get(heatmapCellKey(crit, group));
            const avg = cell ? cell.avg : null;
            const drillable = !!onDrill && !!cell;
            const drill = drillable
              ? () => onDrill!({ type: "criteria", criteria: crit })
              : undefined;
            return (
              <div
                key={crit}
                data-testid="scorecard-row"
                data-criteria={crit}
                className="grid grid-cols-[minmax(110px,34%)_1fr_auto] items-center gap-3"
              >
                <span
                  className="text-xs text-foreground truncate"
                  title={crit}
                >
                  {truncate(labels[crit] ?? crit, 40)}
                </span>

                <div
                  role={drillable ? "button" : undefined}
                  tabIndex={drillable ? 0 : undefined}
                  aria-label={
                    drillable
                      ? `View ${labels[crit] ?? crit} evaluations`
                      : undefined
                  }
                  onClick={drill}
                  onKeyDown={
                    drill
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            drill();
                          }
                        }
                      : undefined
                  }
                  className={`relative h-5 rounded bg-muted overflow-hidden ${
                    drillable
                      ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring"
                      : ""
                  }`}
                  title={
                    cell
                      ? `${crit}: ${cell.avg.toFixed(2)} (n=${cell.count})`
                      : `${crit}: no data`
                  }
                >
                  {avg !== null && (
                    <div
                      className="absolute inset-y-0 left-0 rounded"
                      style={{
                        width: `${avg * 100}%`,
                        background: scoreColorScale(avg),
                      }}
                    />
                  )}
                  {/* attention-threshold marker */}
                  <div
                    className="absolute inset-y-0 w-px bg-foreground/40"
                    style={{ left: `${ATTENTION_SCORE_THRESHOLD * 100}%` }}
                    aria-hidden="true"
                  />
                </div>

                <span className="font-mono text-xs tabular-nums w-9 text-right text-muted-foreground">
                  {avg !== null ? avg.toFixed(2) : "—"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const width = ROW_LABEL_W + groups.length * CELL_W;
  const height = criteria.length * CELL_H;

  return (
    <div
      className="bg-card rounded-lg shadow-sm border border-border p-4 w-fit max-w-full"
      data-testid="heatmap-chart"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
        <h3 className="text-sm font-semibold">Average score by {groupByKey}</h3>
        <div
          className="flex items-center gap-2 text-xs text-muted-foreground"
          data-testid="heatmap-legend"
        >
          <span>0</span>
          <span
            className="inline-block h-2 w-32 rounded"
            style={{ background: SCORE_GRADIENT_CSS }}
            aria-hidden="true"
          />
          <span>1</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        {/* Column headers — tag values. HTML rather than SVG <text> so each one
            carries its own rename control; laid out on the same column geometry
            as the grid below and inside the same scroller, so the two stay
            aligned as it scrolls. */}
        <div
          className="flex text-[11px]"
          style={{
            width,
            paddingLeft: ROW_LABEL_W,
            height: COL_HEADER_H,
            color: chart.axis,
          }}
          data-testid="heatmap-columns"
        >
          {groups.map((group, c) => (
            <div
              key={`col-${c}`}
              className="flex items-end justify-center pb-1.5 min-w-0"
              style={{ width: CELL_W }}
            >
              <TagValue value={group} className="truncate" />
            </div>
          ))}
        </div>

        <svg
          width={width}
          height={height}
          role="img"
          aria-label={`Average score by ${groupByKey} and criteria`}
        >
          {criteria.map((crit, r) => (
            <g key={`row-${r}`}>
              {/* Row label — criteria */}
              <text
                x={ROW_LABEL_W - 10}
                y={r * CELL_H + CELL_H / 2}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={11}
                fill={chart.axis}
              >
                {truncate(labels[crit] ?? crit, 22)}
                <title>{crit}</title>
              </text>

              {groups.map((group, c) => {
                const cell = cells.get(heatmapCellKey(crit, group));
                const x = ROW_LABEL_W + c * CELL_W;
                const y = r * CELL_H;
                const fill = cell ? scoreColorScale(cell.avg) : chart.absent;
                return (
                  <g
                    key={`cell-${r}-${c}`}
                    data-testid="heatmap-cell"
                    data-criteria={crit}
                    data-group={group}
                    data-empty={cell ? undefined : "true"}
                    onClick={
                      onDrill && cell
                        ? () =>
                            onDrill({
                              type: "group",
                              criteria: crit,
                              groupKey: groupByKey,
                              groupValue: group,
                            })
                        : undefined
                    }
                    style={onDrill && cell ? { cursor: "pointer" } : undefined}
                    role={onDrill && cell ? "button" : undefined}
                  >
                    <rect
                      x={x + 1}
                      y={y + 1}
                      width={CELL_W - 2}
                      height={CELL_H - 2}
                      rx={3}
                      fill={fill}
                    />
                    {cell && (
                      <text
                        x={x + CELL_W / 2}
                        y={y + CELL_H / 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={12}
                        fontWeight={500}
                        fill={readableTextColor(fill)}
                      >
                        {cell.avg.toFixed(2)}
                      </text>
                    )}
                    <title>
                      {cell
                        ? `${crit} · ${groupLabel(group)}: ${cell.avg.toFixed(
                            2,
                          )} (n=${cell.count})`
                        : `${crit} · ${groupLabel(group)}: no data`}
                    </title>
                  </g>
                );
              })}
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}
